(function () {
  const header = document.querySelector('[data-bc-header]');
  if (!header) return;

  const state = {
    lastSeenItems: [],
    wishlistCount: 0,
  };

  const lastSeenKey = 'bc_last_seen';

  // Price gate (16. august 2026): B2B status is decided SERVER-SIDE in Liquid
  // and exposed as data-bc-b2b="true" on <html> (layout/theme.liquid), where
  // customer.b2b? is authoritative. Never re-derive this in JS from login
  // state, /cart.js or anything stored in the browser — a browser that once
  // belonged to an approved clinic still holds old localStorage entries with
  // prices in them, and those must not be rendered again. Missing attribute is
  // treated as NOT B2B (fail closed).
  const canShowPrices = document.documentElement.getAttribute('data-bc-b2b') === 'true';

  // Keys that may carry an amount on a stored "last seen" / wishlist record.
  const priceKeys = ['price', 'compare', 'compare_at', 'compareAt', 'compare_at_price', 'price_min', 'price_max'];

  // Returns a copy of a stored record without any price fields. Defensive on
  // purpose: records written by older theme versions have different shapes and
  // some have no price fields at all.
  const stripPrice = (item) => {
    if (!item || typeof item !== 'object') return null;
    const clean = {};
    Object.keys(item).forEach((key) => {
      if (priceKeys.indexOf(key) !== -1) return;
      clean[key] = item[key];
    });
    return clean;
  };

  const drawer = header.querySelector('[data-bc-mobile-drawer]');
  const drawerOverlay = header.querySelector('[data-drawer="overlay"]');
  const lastSeenDrawer = header.querySelector('[data-bc-lastseen-drawer]');
  const wishlistDrawer = header.querySelector('[data-bc-wishlist-drawer]');

  const parseJSON = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (_err) {
      return fallback;
    }
  };

  const publish = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const openMainDrawer = () => {
    if (!drawer || !drawerOverlay) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawerOverlay.hidden = false;
    document.documentElement.setAttribute('data-bc-scroll-lock', 'true');
    publish('bc:drawer:state', { open: true });
  };

  const closeMainDrawer = () => {
    if (!drawer || !drawerOverlay) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawerOverlay.hidden = true;
    document.documentElement.removeAttribute('data-bc-scroll-lock');
    publish('bc:drawer:state', { open: false });
  };

  const openFeatureDrawer = (targetDrawer) => {
    if (!targetDrawer || !drawerOverlay) return;
    targetDrawer.classList.add('is-open');
    targetDrawer.setAttribute('aria-hidden', 'false');
    drawerOverlay.hidden = false;
  };

  const closeFeatureDrawer = (targetDrawer) => {
    if (!targetDrawer || !drawerOverlay) return;
    targetDrawer.classList.remove('is-open');
    targetDrawer.setAttribute('aria-hidden', 'true');
    if (!drawer?.classList.contains('is-open')) {
      drawerOverlay.hidden = true;
    }
  };

  const renderLastSeen = () => {
    const now = Date.now();
    const ttlDays = Number(header.dataset.lastSeenTtlDays || 30);
    const ttlMs = ttlDays * 24 * 60 * 60 * 1000;
    state.lastSeenItems = state.lastSeenItems.filter((item) => now - Number(item.timestamp || 0) <= ttlMs);

    const limit = Number(header.dataset.lastSeenLimit || 10);
    if (state.lastSeenItems.length > limit) {
      state.lastSeenItems = state.lastSeenItems.slice(0, limit);
    }

    // Price gate: for a non-B2B visitor the amounts are stripped out of the
    // stored records before they are written back, so old prices from an
    // earlier approved session do not stay behind on the machine. Product id,
    // handle/url, title and image are kept so the feature still works.
    if (!canShowPrices) {
      state.lastSeenItems = state.lastSeenItems.map(stripPrice).filter(Boolean);
    }

    try {
      localStorage.setItem(lastSeenKey, JSON.stringify(state.lastSeenItems));
    } catch (_err) {}

    const countEls = header.querySelectorAll('[data-lastseen="count"]');
    countEls.forEach((el) => {
      el.textContent = String(state.lastSeenItems.length);
      el.hidden = state.lastSeenItems.length === 0;
    });

    const list = header.querySelector('[data-lastseen="list"]');
    if (!list) return;
    list.innerHTML = '';
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    state.lastSeenItems.forEach((item) => {
      const li = document.createElement('li');
      const href = String(item.url || '/').startsWith('/') ? item.url : '/';
      const imgHTML = item.image
        ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">`
        : '';
      const vendorHTML = item.vendor
        ? `<span class="bc-ls-card__brand">${esc(item.vendor)}</span>`
        : '';
      // Price gate: never render an amount when the server-side flag is false,
      // no matter what a leftover localStorage record still contains.
      const priceHTML = !canShowPrices
        ? ''
        : (item.compare
          ? `<span class="bc-ls-card__price bc-ls-card__price--sale">${esc(item.price)}</span><s class="bc-ls-card__price-compare">${esc(item.compare)}</s>`
          : (item.price ? `<span class="bc-ls-card__price">${esc(item.price)}</span>` : ''));
      const dotClass = item.available !== false ? 'bc-ls-card__dot--green' : 'bc-ls-card__dot--red';
      li.innerHTML = `<a class="bc-ls-card" href="${esc(href)}">`
        + `<div class="bc-ls-card__media">${imgHTML}</div>`
        + `<div class="bc-ls-card__body">`
        + vendorHTML
        + `<span class="bc-ls-card__title">${esc(item.title) || esc(`#${item.id}`)}</span>`
        + (priceHTML ? `<div class="bc-ls-card__price-row">${priceHTML}</div>` : '')
        + `<div class="bc-ls-card__stock"><span class="bc-ls-card__dot ${dotClass}" aria-hidden="true"></span></div>`
        + `</div></a>`;
      list.appendChild(li);
    });
  };

  const renderWishlist = () => {
    const countEls = header.querySelectorAll('[data-wishlist="count"]');
    countEls.forEach((el) => {
      el.textContent = String(state.wishlistCount);
      el.hidden = state.wishlistCount === 0;
    });
  };

  const renderWishlistDrawer = () => {
    if (!wishlistDrawer) return;
    const list = wishlistDrawer.querySelector('[data-wishlist="list"]');
    if (!list) return;
    const items = window.BCWishlist ? window.BCWishlist.getAllWithMeta() : [];
    list.innerHTML = '';
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const renderable = items.filter((item) => item.url && item.title);
    if (!renderable.length) {
      const li = document.createElement('li');
      li.className = 'bc-feature-drawer__empty';
      li.textContent = header.getAttribute('data-wishlist-empty') || 'No saved products yet.';
      list.appendChild(li);
      return;
    }
    renderable.forEach((item) => {
      const li = document.createElement('li');
      const href = String(item.url || '/').startsWith('/') ? item.url : '/';
      const imgHTML = item.image
        ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">`
        : '';
      const vendorHTML = item.vendor
        ? `<span class="bc-ls-card__brand">${esc(item.vendor)}</span>`
        : '';
      li.className = 'bc-wl-item';
      li.innerHTML = `<a class="bc-ls-card" href="${esc(href)}">`
        + `<div class="bc-ls-card__media">${imgHTML}</div>`
        + `<div class="bc-ls-card__body">`
        + vendorHTML
        + `<span class="bc-ls-card__title">${esc(item.title) || esc(item.id)}</span>`
        // Price gate: same server-side flag. bc-wishlist.js already scrubs the
        // stored meta for non-B2B visitors; this is the second lock so a stale
        // record can never paint an amount into the drawer.
        + (canShowPrices && item.price ? `<div class="bc-ls-card__price-row"><span class="bc-ls-card__price">${esc(item.price)}</span></div>` : '')
        + `</div></a>`
        + `<button class="bc-wl-remove" type="button" data-wishlist-remove="${esc(item.id)}" aria-label="${esc(wishlistDrawer.getAttribute('data-wishlist-remove-label') || 'Remove')}">`
        + `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`
        + `</button>`;
      list.appendChild(li);
    });
  };

  const trackProductView = () => {
    const productRoot = document.querySelector('[data-bc-product-id]');
    if (!productRoot) return;
    const id = productRoot.getAttribute('data-bc-product-id');
    if (!id) return;

    const current = {
      id,
      title: productRoot.getAttribute('data-bc-product-title') || '',
      url: window.location.pathname,
      image: productRoot.getAttribute('data-bc-product-image') || '',
      price: productRoot.getAttribute('data-bc-product-price') || '',
      compare: productRoot.getAttribute('data-bc-product-compare') || '',
      vendor: productRoot.getAttribute('data-bc-product-vendor') || '',
      available: productRoot.getAttribute('data-bc-product-available') !== 'false',
      timestamp: Date.now(),
    };

    state.lastSeenItems = state.lastSeenItems.filter((item) => String(item.id) !== String(id));
    // Price gate: for a non-B2B visitor the amount is never written to
    // localStorage in the first place.
    state.lastSeenItems.unshift(canShowPrices ? current : stripPrice(current));
    renderLastSeen();
    publish('bc:lastseen:updated', { count: state.lastSeenItems.length, ids: state.lastSeenItems.map((item) => item.id) });
  };

  const syncCart = async () => {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      const countEls = header.querySelectorAll('[data-cart="count"]');
      countEls.forEach((el) => (el.textContent = String(cart.item_count || 0)));
      publish('bc:cart:updated', { count: cart.item_count || 0, total: cart.total_price || 0 });
    } catch (_err) {}
  };

  const hydrate = () => {
    // Defensive: the stored value may be invalid JSON, a non-array, or contain
    // null/primitive entries written by an older theme version.
    const stored = parseJSON(localStorage.getItem(lastSeenKey), []);
    state.lastSeenItems = Array.isArray(stored)
      ? stored.filter((item) => item && typeof item === 'object')
      : [];
    renderLastSeen();
    renderWishlist();
  };

  header.addEventListener('click', (event) => {
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      const id = removeBtn.getAttribute('data-wishlist-remove');
      if (window.BCWishlist) window.BCWishlist.remove(id);
      renderWishlistDrawer();
      return;
    }

    const cartTrigger = event.target.closest('[data-cart="open"]');
    if (cartTrigger) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('bc:cart:open'));
      return;
    }

    const target = event.target.closest('[data-drawer],[data-lastseen],[data-wishlist]');
    if (!target) return;

    const drawerAction = target.getAttribute('data-drawer');
    if (drawerAction === 'open') openMainDrawer();
    if (drawerAction === 'close' || drawerAction === 'overlay') closeMainDrawer();

    const lastSeenAction = target.getAttribute('data-lastseen');
    if (lastSeenAction === 'open') openFeatureDrawer(lastSeenDrawer);
    if (lastSeenAction === 'close') closeFeatureDrawer(lastSeenDrawer);

    const wishlistAction = target.getAttribute('data-wishlist');
    if (wishlistAction === 'open') {
      document.dispatchEvent(new CustomEvent('bc:wishlist:open'));
    }
    if (wishlistAction === 'close') closeFeatureDrawer(wishlistDrawer);
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-wishlist]');
    if (!target || header.contains(target)) return;

    const wishlistAction = target.getAttribute('data-wishlist');
    if (wishlistAction === 'open') {
      document.dispatchEvent(new CustomEvent('bc:wishlist:open'));
    }
  });

  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', () => {
      closeMainDrawer();
      closeFeatureDrawer(lastSeenDrawer);
      closeFeatureDrawer(wishlistDrawer);
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (document.documentElement.hasAttribute('data-bc-cart-drawer-open')) return;
    closeMainDrawer();
    closeFeatureDrawer(lastSeenDrawer);
    closeFeatureDrawer(wishlistDrawer);
  });

  document.addEventListener('bc:wishlist:open', () => {
    renderWishlistDrawer();
    openFeatureDrawer(wishlistDrawer);
  });

  const moneyFormat = header.getAttribute('data-money-format') || '{{amount}}';

  const formatMoney = (cents) => {
    const amount = cents / 100;
    const fmt = (decimals, thousandsSep, decimalSep) => {
      const parts = amount.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\\B(?=(\\d{3})+(?!\\d))/g, thousandsSep);
      return decimals > 0 ? parts.join(decimalSep) : parts[0];
    };
    return moneyFormat
      .replace(/\\{\\{\\s*amount\\s*\\}\\}/,                                  fmt(2, ',', '.'))
      .replace(/\\{\\{\\s*amount_no_decimals\\s*\\}\\}/,                      fmt(0, ',', '.'))
      .replace(/\\{\\{\\s*amount_with_comma_separator\\s*\\}\\}/,             fmt(2, '.', ','))
      .replace(/\\{\\{\\s*amount_no_decimals_with_comma_separator\\s*\\}\\}/, fmt(0, '.', ','))
      .replace(/\\{\\{\\s*amount_with_apostrophe_separator\\s*\\}\\}/,        fmt(2, "'", '.'))
      .replace(/\\{\\{\\s*amount_with_space_separator\\s*\\}\\}/,             fmt(2, ' ', ','))
      .replace(/\\{\\{\\s*amount_no_decimals_with_space_separator\\s*\\}\\}/, fmt(0, ' ', ','));
  };

  document.addEventListener('bc:cart:updated', (event) => {
    const count = Number(event.detail?.count || 0);
    const total = Number(event.detail?.total || 0);
    header.querySelectorAll('[data-cart="count"]').forEach((el) => {
      el.textContent = String(count);
    });
    // Price gate (16. august 2026): snippets/bc-header-utilities.liquid renders
    // the cart total empty for non-B2B customers and stamps
    // data-cart-total-gated="true" on that element. Skip those elements here so
    // syncCart() does not refill the amount milliseconds after page load. The
    // decision is made server-side in Liquid, where customer.b2b? is
    // authoritative — never re-derive it in JS from login state or /cart.js.
    header.querySelectorAll('[data-cart="total"]:not([data-cart-total-gated])').forEach((el) => {
      el.textContent = formatMoney(total);
    });
  });

  document.addEventListener('bc:wishlist:updated', (event) => {
    state.wishlistCount = Number(event.detail?.count || 0);
    renderWishlist();
    if (wishlistDrawer && wishlistDrawer.classList.contains('is-open')) {
      renderWishlistDrawer();
    }
  });

  const initUspRotators = () => {
    const rotators = header.querySelectorAll('[data-bc-usp-rotator]');
    rotators.forEach((rotator) => {
      const items = Array.from(rotator.querySelectorAll('.bc-topbar__usp-item'));
      if (items.length < 2) return;

      let active = items.findIndex((el) => el.classList.contains('is-active'));
      if (active < 0) active = 0;
      let timer = null;
      let paused = false;

      const showAt = (index) => {
        items.forEach((el, i) => {
          const isActive = i === index;
          el.classList.toggle('is-active', isActive);
          if (isActive) {
            el.removeAttribute('aria-hidden');
          } else {
            el.setAttribute('aria-hidden', 'true');
          }
        });
        active = index;
      };

      const advance = () => {
        if (paused) return;
        showAt((active + 1) % items.length);
      };

      const start = () => {
        if (timer) return;
        timer = window.setInterval(advance, 3000);
      };

      const stop = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      };

      rotator.addEventListener('mouseenter', () => { paused = true; });
      rotator.addEventListener('mouseleave', () => { paused = false; });
      rotator.addEventListener('focusin', () => { paused = true; });
      rotator.addEventListener('focusout', () => { paused = false; });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else start();
      });

      start();
    });
  };

  hydrate();
  trackProductView();
  syncCart();
  initUspRotators();
})();
